import { injectText } from '../i18n/translate-text';
import {
  editorStateFromJob,
  editorStateToJobPatch,
} from '../core/levels/job-levels-editor-state';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import {
  catchError,
  concat,
  firstValueFrom,
  forkJoin,
  from,
  last,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import {
  Job,
  JobActivityRange,
  JobContact,
  JobKinds,
  JobRequirements,
  JobStati,
} from '../types/job';

import { DatePipe } from '@angular/common';
import { School, SchoolContact } from '../types/school';
import { SchoolService } from './school.service';
import { toApiRichTextPair } from '../core/richText';
import { SchoolApiService } from './school-api.service';
import { SchoolAuthorityApiService } from './school-authority-api.service';
import { environment } from '../../environments/environment';
import { PUBLIC_API_REQUEST } from '../core/public-api-request.context';
import { fromApiDateString, toApiDateString } from '../core/helpers';
import {
  resolveSubstituteDurationFlags,
  writeStoredDurationFlags,
} from '../core/job-duration-flags';

/**
 * Older postings pre-date `start_date_type`; a present `from_date` on a permanent
 * job means DATE, nothing set means "not configured".
 */
export function resolvePermanentStartDateType(
  apiType: StartDateType | null | undefined,
  hasFromDate: boolean
): StartDateType | null {
  return apiType ?? (hasFromDate ? 'DATE' : null);
}
import {
  EDUCATION_LEVEL_MAP,
  REVERSE_EDUCATION_LEVEL_MAP,
  REVERSE_SCHOOL_LEVEL_MAP,
  SCHOOL_LEVEL_MAP,
} from '../types/enum-maps';
import { fileRefToBucketFile } from '../core/file-utils';
import { apiVideoAssetFromApiIds } from '../core/api-video-assets';
import { FileApiService } from './file-api.service';
import { getLevelsOrder as getLevelsOrderUtil } from '../core/level-utils';
import { formatJobLevelLines } from '../core/levels/level-format';
import { localBlobUrlCache } from '../core/api-video-assets';
import {
  ApiSchoolPermissionUser,
  CloseJobPostingReq,
  CreateJobPostingReq,
  IdRes,
  JobSearchResponse,
  LevelMatch,
  JobTitleFacet,
  JobTitleFacetList,
  NewJobPostingContact,
  ReviewApplicationReq,
  SchoolAuthorityPermission,
  SchoolContactDetail,
  SchoolPermissionApiResponse,
  StatusRes,
  UpdateActivityReq,
  UpdateDeadlineReq,
  UpdateDocTitleReq,
  UpdateEducationLevelsReq,
  UpdateEmploymentPeriodReq,
  StartDateType,
  UpdateGalleryItemReq,
  ReorderGalleryReq,
  UpdateJobPostingAdditionalInfoReq,
  UpdateJobPostingContactsReq,
  UpdateJobPostingJobTasksReq,
  UpdateJobPostingSkillRequirementsReq,
  UpdateJobPostingSubjectsReq,
  UpdateJobPostingContactItem,
  UpdateJobPostingWhatWeOfferReq,
  UpdateContentLanguageReq,
  UpdateJobTitleReq,
  UpdatePublishFromReq,
  UpdatePublishToReq,
  UpdatePublishWindowReq,
  UpdateQualificationsReq,
  UpdateWorkDayMatrixReq,
  VideoUploadTokenRes,
} from '../types/rest-api';
import { mapApiWorkDayMatrixToArray } from '../utils/location-mapper';
import { ProfileApiService } from './profile-api.service';
import { resolveAdminJobFunction } from '../utils/admin-contact-job-function';
import { mapSchoolContactDetail } from '../utils/school-contact-mapper';
import {
  applyGalleryUrls,
  galleryFileIds,
  mapGalleryItem,
} from '../utils/gallery-mapper';

/** Filters of the public job search (GET /v1/job-postings). */
export interface JobSearchParams {
  query?: string;
  cantons?: string[];
  educationLevels?: string[];
  schoolLevels?: string[];
  /** Berufsbezeichnungen as displayed (template or free text), see JobTitleFacet. */
  jobTitles?: string[];
  zipcode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  radiusInKm?: number;
  jobPostingTypes?: string[];
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
  grades?: string[];
  sek1Models?: string[];
  levelMatch?: LevelMatch;
  /** A Schuleinheit with its Standorte. */
  schoolId?: string;
  /** A Schulgemeinde on all levels. */
  schoolAuthorityId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class JobService {
  private readonly apiBase = environment.apiBaseUrl;
  private http = inject(HttpClient);
  /**
   * Set once `PUT /publish-window` has answered "no such route", so the rest of
   * the session goes straight to the two-request fallback. Reset by a reload,
   * which is what happens anyway once the newer backend is serving.
   */
  private publishWindowUnavailable = false;
  private datePipe = inject(DatePipe);
  private text = injectText();
  private schoolService = inject(SchoolService);
  private schoolApi = inject(SchoolApiService);
  private schoolAuthorityApi = inject(SchoolAuthorityApiService);
  private fileApi = inject(FileApiService);
  private profileApi = inject(ProfileApiService);

  /**
   * Public wrapper around the internal file-URL resolver for callers (e.g.
   * ApplicationService) that build {@link Job} objects via the application
   * mappers and need the same avatar / background / media URL hydration.
   */
  resolveJobFileUrlsPublic(jobs: Job[]): Observable<Job[]> {
    return this.resolveJobFileUrlsWithAvatarRecovery(jobs);
  }

  /**
   * When the posting snapshot carries a {@link Job.schoolAvatar} file id that no longer
   * resolves (stale summary), reload owner media from the school/authority API and retry
   * signed URL resolution once.
   */
  private resolveJobFileUrlsWithAvatarRecovery(jobs: Job[]): Observable<Job[]> {
    return this.resolveJobFileUrls(jobs).pipe(
      mergeMap(resolved => {
        const broken = resolved.filter(
          j => !!j.schoolAvatar?.filename && !j.schoolAvatar?.path
        );
        if (broken.length === 0) {
          return of(resolved);
        }
        return forkJoin(
          broken.map(j => this.hydrateJobOwnerProfilePictures(j))
        ).pipe(mergeMap(() => this.resolveJobFileUrls(resolved)));
      })
    );
  }

  /**
   * Lightweight resolver for HR list cards: only fills `schoolAvatar.path`
   * (and `schoolBackgroundImage.path`) for postings whose `school` snapshot
   * lacks an inline {@link FileRef} URL. One batched `GET /v1/files/{id}` per
   * unique id, no per-job `school.getById` recovery — postings without a
   * resolvable logo fall back to the default avatar in {@link getJobPostingDisplayLogoUrl}.
   */
  private resolveJobOwnerLogosOnly(jobs: Job[]): Observable<Job[]> {
    const fileIds = jobs.flatMap(j => [
      j.schoolAvatar?.path ? undefined : j.schoolAvatar?.filename,
      j.schoolBackgroundImage?.path
        ? undefined
        : j.schoolBackgroundImage?.filename,
    ]);
    const idsToResolve = fileIds.filter((id): id is string => !!id);
    if (idsToResolve.length === 0) {
      return of(jobs);
    }
    return this.fileApi.resolveFileUrls(idsToResolve).pipe(
      map(urlMap => {
        for (const j of jobs) {
          if (j.schoolAvatar?.filename && !j.schoolAvatar.path) {
            const url = urlMap.get(j.schoolAvatar.filename);
            if (url) j.schoolAvatar = { ...j.schoolAvatar, path: url };
          }
          if (
            j.schoolBackgroundImage?.filename &&
            !j.schoolBackgroundImage.path
          ) {
            const url = urlMap.get(j.schoolBackgroundImage.filename);
            if (url)
              j.schoolBackgroundImage = {
                ...j.schoolBackgroundImage,
                path: url,
              };
          }
        }
        return jobs;
      })
    );
  }

  private resolveJobFileUrls(jobs: Job[]): Observable<Job[]> {
    // Skip ids where the inline FileRef already gave us a URL — `path` is set
    // for owner avatar/background images and `avatarUrl` already starts with
    // `http(s)://` for contacts hydrated from `profile_picture` /
    // `outside_profile_picture`.
    const fileIds = jobs.flatMap(j => [
      j.schoolAvatar?.path ? undefined : j.schoolAvatar?.filename,
      j.schoolBackgroundImage?.path
        ? undefined
        : j.schoolBackgroundImage?.filename,
      ...(j.contacts || []).map(c =>
        c.avatarUrl && /^https?:\/\//.test(c.avatarUrl)
          ? undefined
          : c.avatarUrl
      ),
      ...galleryFileIds(j.media || []),
      ...(j.additionalDocuments || []).map(d => d.filename),
      ...(j.video?.id && !j.video.hls ? [j.video.id] : []),
      ...(j.video?.poster && !j.video.poster.startsWith('http')
        ? [j.video.poster]
        : []),
    ]);
    return this.fileApi.resolveFileUrls(fileIds).pipe(
      map(urlMap => {
        for (const j of jobs) {
          if (j.schoolAvatar?.filename && !j.schoolAvatar.path) {
            const url = urlMap.get(j.schoolAvatar.filename);
            if (url) j.schoolAvatar = { ...j.schoolAvatar, path: url };
          }
          if (
            j.schoolBackgroundImage?.filename &&
            !j.schoolBackgroundImage.path
          ) {
            const url = urlMap.get(j.schoolBackgroundImage.filename);
            if (url)
              j.schoolBackgroundImage = {
                ...j.schoolBackgroundImage,
                path: url,
              };
          }
          for (const c of j.contacts || []) {
            if (c.avatarUrl && !/^https?:\/\//.test(c.avatarUrl)) {
              const url = urlMap.get(c.avatarUrl);
              if (url) c.avatarUrl = url;
            }
          }
          applyGalleryUrls(j.media || [], urlMap);
          for (const d of j.additionalDocuments || []) {
            if (d.filename) {
              const url = urlMap.get(d.filename);
              if (url) d.url = url;
            }
          }
          if (j.video?.id) {
            let video = j.video;
            const blobUrl = localBlobUrlCache.get(video.id);
            if (blobUrl) {
              video = { ...video, hls: '', mp4: blobUrl, poster: null };
            } else {
              if (!video.hls) {
                const hlsUrl = urlMap.get(video.id);
                if (hlsUrl) video = { ...video, hls: hlsUrl };
              }
              if (video.poster && !video.poster.startsWith('http')) {
                const posterUrl = urlMap.get(video.poster);
                if (posterUrl) video = { ...video, poster: posterUrl };
              }
            }
            j.video = video;
          }
        }
        return jobs;
      })
    );
  }

  /**
   * Derives {@link JobStati} for HR list/detail payloads.
   *
   * Backend publishing is date-driven: when `publish_to` passes, the API flips
   * `is_published` to `false` without setting `is_closed`. Without date-aware
   * fallback, an expired posting has all three booleans false and would land
   * in {@link JobStati.DRAFT}. HR groups by status, so an expired posting must
   * resolve to {@link JobStati.DEACTIVATED}; a future `publish_from` must
   * resolve to {@link JobStati.PENDING}.
   *
   * Explicit flags (`is_deleted` / `is_archived` / `is_closed`) still win — they
   * capture deliberate admin actions and shouldn't be second-guessed by dates.
   *
   * NOTE — {@link JobStati.PENDING} is not a backend state. The API has no status
   * field at all (see openapi.json: only `is_published` / `is_closed` /
   * `is_archived` + the two dates), so "geplant" is just *published with a future
   * embargo*. Moving `publish_from` to today therefore publishes the posting; that
   * is the data model, not a bug in this function. Repeated attempts to fake a
   * "scheduled but unconfirmed" state here — a session-scoped Set of ids, `>=` vs
   * `>` — all regressed, because client-only state dies on reload and never
   * reaches a second device. The user-facing guard now lives in
   * JobRuntimeFormComponent (a confirm dialog before a same-day `publish_from`).
   * A durable fix needs a backend flag or an unpublish endpoint.
   *
   * The *lapsed* case has that endpoint, though: a posting past its `publish_to`
   * is still `is_published`, so its DEACTIVATED here is derived from the date and
   * is reversible — extending the window silently puts it back in front of the
   * public. {@link closeIfExpired} promotes that derived state to a real
   * `is_closed` via `PUT …/close` before the dates move, so the `is_closed`
   * branch above wins and re-publishing takes the explicit button. That is
   * durable state on the backend, not a client-only flag.
   */
  private resolveJobStatusFromApi(apiJob: any): Job['status'] {
    if (apiJob.is_deleted) return JobStati.DELETED;
    if (apiJob.is_archived) return JobStati.ARCHIVED;
    if (apiJob.is_closed) return JobStati.DEACTIVATED;

    const today = this.todayLocalIsoDate();
    const pubFrom: string | undefined = apiJob.publish_from || undefined;
    const pubTo: string | undefined = apiJob.publish_to || undefined;

    if (pubFrom && pubFrom > today && apiJob.is_published) {
      return JobStati.PENDING;
    }
    if (pubTo && pubTo < today) return JobStati.DEACTIVATED;
    if (apiJob.is_published) return JobStati.PUBLISHED;
    return JobStati.DRAFT;
  }

  /**
   * True when a posting may be shown to the public — at its own URL and in public
   * lists (school profile, embed widget).
   *
   * Prefer this over the raw `isPublic` / `isClosed` booleans. The API keeps
   * `is_published: true` after `publish_to` passes and never sets `is_closed`, so
   * the booleans alone report an expired posting as live. {@link resolveJobStatusFromApi}
   * already folds a passed `publish_to` (→ DEACTIVATED) and a future `publish_from`
   * (→ PENDING) into `status`, which is what the HR overview groups by.
   *
   * Same rule as {@link JobHeaderComponent.showApplyButton}, applied to the whole page.
   *
   * PENDING (scheduled, `publish_from` in the future) is hidden here too, but the
   * detail page gives it its own copy — see JobUnavailableComponent's `pending`
   * variant. This predicate answers "may the public see it", not "why not".
   */
  isPubliclyVisible(job: Job): boolean {
    return job.status === JobStati.PUBLISHED;
  }

  // Local-date YYYY-MM-DD to compare against `publish_from` / `publish_to`
  // (`date` format, no time) without UTC off-by-one near midnight in CH.
  private todayLocalIsoDate(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private mapApiJobToJob(apiJob: any): Job {
    const t = apiJob.job_posting_type as unknown;
    const isPermanent =
      t === 'PERMANENT' || t === 'FESTANSTELLUNG' || t === 'FULL_TIME';
    const isTemporary =
      t === 'TEMPORARY' || t === 'BEFRISTETE_ANSTELLUNG' || t === 'TEMP';
    const kind: Job['kind'] = isPermanent
      ? JobKinds.PERMANENT
      : isTemporary
        ? JobKinds.TEMPORARY
        : JobKinds.SUBSTITUTE;
    const school = apiJob.school || null;
    const authority = apiJob.school_authority || null;
    const owner = school ?? authority;
    const ownerType: 'school' | 'school_authority' = school
      ? 'school'
      : 'school_authority';
    const ownerName = owner?.name ?? '';
    const ownerAvatarId =
      owner?.profile_picture_id ??
      (owner as { profilePictureId?: string | null } | null)
        ?.profilePictureId ??
      null;
    const ownerWallId =
      owner?.wall_picture_id ??
      (owner as { wallPictureId?: string | null } | null)?.wallPictureId ??
      null;
    // `apiJob` is `any`-typed here so we read both shapes defensively. Prefer the
    // Prefer the directly-resolved ref; fall back to root school's picture for Standorte.
    const ownerAvatarRef = (owner?.profile_picture ??
      owner?.root_profile_picture ??
      null) as import('../types/rest-api').FileRef | null;
    const ownerWallRef = (owner?.wall_picture ??
      owner?.root_wall_picture ??
      null) as import('../types/rest-api').FileRef | null;
    const ownerLocation = owner?.location ?? null;

    // `published_at` is a full ISO datetime (set by the backend on publish).
    // Stored as Unix seconds for the JobPosting `datePosted` field.
    const publishedDate = apiJob.published_at
      ? new Date(apiJob.published_at)
      : undefined;
    const publishedOnUnix =
      publishedDate && !isNaN(publishedDate.getTime())
        ? Math.floor(publishedDate.getTime() / 1000)
        : undefined;

    return {
      id: apiJob.id,
      schoolId: school ? apiJob.school_id || school.id || '' : '',
      schoolRootId: school
        ? apiJob.school_root_id ||
          school.parent_id ||
          school.id ||
          apiJob.school_id ||
          ''
        : '',
      schoolAuthorityId: authority
        ? apiJob.school_authority_id || authority.id || null
        : null,
      ownerType,
      schoolName: ownerName,
      schoolTitle: '',
      schoolAvatar: fileRefToBucketFile(ownerAvatarRef, ownerAvatarId),
      schoolBackgroundImage: fileRefToBucketFile(ownerWallRef, ownerWallId),
      schoolAddress: ownerLocation
        ? {
            street: ownerLocation.street ?? undefined,
            streetNumber: ownerLocation.street_number ?? undefined,
            zipcode: ownerLocation.zipcode,
            city: ownerLocation.city,
            canton: ownerLocation.canton,
            cantonShort: ownerLocation.canton_short,
            municipality: ownerLocation.municipality,
          }
        : null,
      language: apiJob.language ?? undefined,
      title: apiJob.job_title || apiJob.custom_job_title || '',
      function: apiJob.job_title || apiJob.custom_job_title || '',
      jobTitleId: apiJob.job_title_id ?? null,
      isFullTimeJob: isPermanent,
      kind,
      durationFrom: fromApiDateString(apiJob.from_date),
      durationTo: fromApiDateString(apiJob.to_date),
      ...(() => {
        if (isPermanent) {
          const startDateType = resolvePermanentStartDateType(
            apiJob.start_date_type,
            !!apiJob.from_date
          );
          return {
            startDateType,
            isDurationStartNow: startDateType === 'IMMEDIATELY',
            isDurationEndOpen: false,
          };
        }
        const flags = resolveSubstituteDurationFlags(
          apiJob.id,
          !!apiJob.from_date,
          !!apiJob.to_date,
          !!apiJob.is_published
        );
        return {
          isDurationStartNow: flags.isDurationStartNow,
          isDurationEndOpen: flags.isDurationEndOpen,
        };
      })(),
      isClosed: apiJob.is_closed ?? false,
      // Listing visibility: a closed posting is not "public" even if `is_published` stayed true in the API.
      isPublic: !!(apiJob.is_published && !apiJob.is_closed),
      isDeleted: apiJob.is_deleted ?? false,
      status: this.resolveJobStatusFromApi(apiJob),
      countApplications: apiJob.count_applications ?? 0,
      countUnreadApplications: apiJob.count_unread_applications ?? 0,
      schoolLevels: (apiJob.education_levels || [])
        .map((l: string) => EDUCATION_LEVEL_MAP[l] || l)
        .filter(Boolean),
      schoolLevelDescriptions: (apiJob.school_levels || [])
        .map((l: string) => SCHOOL_LEVEL_MAP[l] || l)
        .filter(Boolean),
      levelMode: apiJob.level_mode ?? null,
      grades: apiJob.grades ?? [],
      sek1Models: apiJob.sek1_models ?? [],
      subjects: (apiJob.subjects || [])
        .map((s: { name?: string }) => s.name)
        .filter(Boolean),
      subjectIds: (apiJob.subjects || [])
        .map((s: { id?: string }) => s.id)
        .filter(Boolean),
      yourTasks: apiJob.job_tasks || undefined,
      yourTasks_html: apiJob.job_tasks_html || undefined,
      yourSkills: apiJob.skill_requirements || undefined,
      yourSkills_html: apiJob.skill_requirements_html || undefined,
      weOffer: apiJob.what_we_offer || undefined,
      weOffer_html: apiJob.what_we_offer_html || undefined,
      whatWeOfferHighlights: apiJob.what_we_offer_highlights || [],
      whatWeOfferHighlightIcons: apiJob.what_we_offer_highlight_icons || {},
      additionalInfos: apiJob.additional_description || undefined,
      additionalInfos_html: apiJob.additional_description_html || undefined,
      workdayMatrix: mapApiWorkDayMatrixToArray(apiJob.work_day_matrix),
      ...this.mapApiActivityRanges(apiJob),
      activityRangeInPercentage:
        apiJob.activity_percentage_from != null ||
        apiJob.activity_percentage_to != null
          ? {
              from: apiJob.activity_percentage_from,
              to: apiJob.activity_percentage_to,
            }
          : undefined,
      activityRangeInHours:
        apiJob.activity_hours_from != null || apiJob.activity_hours_to != null
          ? { from: apiJob.activity_hours_from, to: apiJob.activity_hours_to }
          : undefined,
      applicationEndDate: fromApiDateString(apiJob.deadline),
      publishedOnUnix,
      // Used by job detail header + cards for "Vor etwa …"
      // Prefer publish_from (OpenAPI), fall back to other common timestamp fields.
      modifiedOn:
        apiJob.publish_from ||
        apiJob.modified_on ||
        apiJob.updated_at ||
        apiJob.updated_on ||
        apiJob.created_at ||
        apiJob.created_on ||
        apiJob.from_date ||
        undefined,
      licenseExpiresOn: apiJob.license_expires_on ?? null,
      publishesWithFreeLicense: apiJob.publishes_with_free_license ?? false,
      runtimeStartTimestamp: fromApiDateString(apiJob.publish_from)?.getTime(),
      runtimeEndTimestamp: fromApiDateString(apiJob.publish_to)?.getTime(),
      contacts: (apiJob.contacts || []).map((c: any) => {
        const isAdmin = !!c.user_id;
        return {
          // Only real users have a `user_id` (see OpenAPI `NewJobPostingContact`).
          // Outside contacts only have an `id`; keep a stable synthetic key for tracking.
          userId: isAdmin ? c.user_id : `outside:${c.id || ''}`,
          contactId: c.id,
          isAdmin,
          name:
            c.outside_full_name ||
            `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
            c.full_name ||
            c.name ||
            '',
          email: c.email || '',
          phone: c.phone || '',
          jobFunction: c.title || c.job_function || '',
          visible: true,
          // Prefer inline FileRef.url so we can skip the per-image
          // GET /v1/files/{id} round-trip; fall back to the deprecated id paths
          // (incl. `outside_profile_picture` for outside contacts) when stale.
          avatarUrl:
            c.profile_picture?.url ??
            c.outside_profile_picture?.url ??
            (c.profile_picture_id || c.outside_profile_picture_id || undefined),
        };
      }),
      media: (apiJob.gallery || []).map(mapGalleryItem),
      additionalDocuments: (apiJob.docs || []).map((d: any) => ({
        path: '',
        filename: d.doc_id,
        title: d.title,
        docId: d.id,
      })),
      video: apiVideoAssetFromApiIds(
        apiJob.video_id,
        apiJob.video_thumbnail_id
      ),
      socialLinks: apiJob.website_urls || [],
      requirements: this.mapApiQualificationsToRequirements(
        apiJob.qualifications
      ),
    } as unknown as Job;
  }

  /**
   * Map GET /v1/job-applications and job-posting applications list items
   * (OpenAPI `JobApplicationSummary`) to a {@link Job} for cards and shared UI.
   */
  mapJobApplicationSummaryToJob(summary: any): Job {
    const s = summary as any;
    return this.mapApiJobToJob({
      id: s.job_posting_id,
      school_id: s.school?.id ?? null,
      school_root_id: s.school?.parent_id ?? s.school?.id ?? null,
      school: s.school ?? null,
      school_authority_id: s.school_authority?.id ?? null,
      school_authority: s.school_authority ?? null,
      job_title: s.job_title,
      custom_job_title: s.custom_job_title,
      job_title_id: s.job_title_id,
      job_posting_type: s.job_posting_type,
      education_levels: s.education_levels,
      school_levels: s.school_levels,
      from_date: s.from_date,
      to_date: s.to_date,
      is_closed: s.is_closed,
      is_published: s.is_published,
      deadline: s.deadline,
      publish_from: s.publish_from,
      publish_to: s.publish_to,
      activity_percentage_from: s.activity_percentage_from,
      activity_percentage_to: s.activity_percentage_to,
      activity_hours_from: s.activity_hours_from,
      activity_hours_to: s.activity_hours_to,
    });
  }

  private mapApiQualificationsToRequirements(
    qualifications: string[] | null | undefined
  ): JobRequirements | undefined {
    if (!qualifications?.length) return undefined;
    return {
      baseYear: qualifications.includes('BASE_YEAR_COMPLETED'),
      diplom: qualifications.includes('DIPLOMA'),
      student: qualifications.includes('TRAINING'),
    };
  }

  /**
   * Chains discard calls so only one discardJob request is in flight at a time.
   * Prevents overlapping deletes that cause loading loop or false success.
   */
  private lastDiscardPromise: Promise<unknown> = Promise.resolve();

  // --- Attributes ---

  /**
   * GET /v1/attributes?types=SUBJECT – fetch subject catalog from API.
   *
   * The OpenAPI parameter name is `types` (FastAPI repeats the key for arrays);
   * `types[]` is silently ignored, which would return *all* attribute types and
   * cause the backend to reject non-SUBJECT ids on PUT …/subjects with
   * "Invalid subject_ids". The client-side `type === 'SUBJECT'` filter is a
   * defensive guard in case the server ever loosens the filter.
   */
  getSubjectAttributes(): Observable<{ id: string; name: string }[]> {
    return this.http
      .get<
        { id: string; name: string; type: string }[]
      >(`${this.apiBase}/v1/attributes`, { params: new HttpParams().append('types', 'SUBJECT') })
      .pipe(map(attrs => attrs.filter(a => a.type === 'SUBJECT')));
  }

  // --- Job Postings ---

  /** GET /v1/job-postings – list all job postings */
  list(): Observable<unknown> {
    return this.http.get(`${this.apiBase}/v1/job-postings`);
  }

  /**
   * GET /v1/job-postings/schools/{school_id}
   * - forProfile=true (default): breadcrumb profile scope; guests get published postings only.
   * - forProfile=false: exact owner school_id (admin / HR).
   * - asPublicVisitor=true: omit Bearer; profile pages always use this for published-only listings.
   */
  getBySchool(
    schoolId: string,
    options?: { forProfile?: boolean; asPublicVisitor?: boolean }
  ): Observable<Job[]> {
    const forProfile = options?.forProfile ?? true;
    const context = options?.asPublicVisitor
      ? new HttpContext().set(PUBLIC_API_REQUEST, true)
      : undefined;
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${schoolId}`, {
        params: { for_profile: forProfile },
        context,
      })
      .pipe(
        map(jobs => jobs.map(j => this.mapApiJobToJob(j))),
        mergeMap(jobs => this.enrichJobsWithOwnerAvatars(jobs)),
        mergeMap(jobs => this.resolveJobFileUrlsWithAvatarRecovery(jobs))
      );
  }

  /** GET /v1/job-postings/school-authorities/{school_authority_id} – list job postings owned directly by an authority. */
  getBySchoolAuthority(
    authorityId: string,
    options?: { hydrateFiles?: boolean }
  ): Observable<Job[]> {
    const hydrateFiles = options?.hydrateFiles ?? true;
    const base$ = this.http
      .get<
        any[]
      >(`${this.apiBase}/v1/job-postings/school-authorities/${authorityId}`)
      .pipe(map(jobs => jobs.map(j => this.mapApiJobToJob(j))));
    if (!hydrateFiles) {
      return base$.pipe(mergeMap(jobs => this.resolveJobOwnerLogosOnly(jobs)));
    }
    return base$.pipe(
      mergeMap(jobs => this.enrichJobsWithOwnerAvatars(jobs)),
      mergeMap(jobs => this.resolveJobFileUrlsWithAvatarRecovery(jobs))
    );
  }

  /**
   * Lightweight authority posting list (no avatar/file hydration) for counting
   * KPIs such as "Offene Stellen". Status is still resolved via
   * {@link mapApiJobToJob}, so callers can filter by {@link JobStati}.
   */
  listBySchoolAuthorityLite(authorityId: string): Observable<Job[]> {
    return this.http
      .get<
        any[]
      >(`${this.apiBase}/v1/job-postings/school-authorities/${authorityId}`)
      .pipe(map(jobs => jobs.map(j => this.mapApiJobToJob(j))));
  }

  /**
   * Lightweight school posting list (no avatar/file hydration) for counting
   * KPIs. `for_profile=false` returns the owner-school admin view (all statuses,
   * not just published) so counts reflect every posting the school owns.
   */
  listBySchoolLite(schoolId: string): Observable<Job[]> {
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${schoolId}`, {
        params: { for_profile: false },
      })
      .pipe(map(jobs => jobs.map(j => this.mapApiJobToJob(j))));
  }

  /**
   * Lightweight, profile-scoped posting list (no avatar/file hydration) for the
   * "Offene Stellen" KPI. Mirrors the public profile job listing
   * ({@link SchoolJobsCardComponent}: `getBySchool(id, { forProfile: true,
   * asPublicVisitor: true })`): `for_profile=true` on the schools endpoint
   * returns the school AND everything beneath it (its locations) as the public
   * visitor sees it. Use this per school unit so the count includes
   * location-level postings, then filter callers by `isPublic` to match exactly
   * what the profile shows. (Note: passing an authority id here returns only the
   * authority's own postings — count authority-level postings separately via
   * {@link listBySchoolAuthorityLite}.)
   */
  listProfileScopedLite(schoolId: string): Observable<Job[]> {
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${schoolId}`, {
        params: { for_profile: true },
        context: new HttpContext().set(PUBLIC_API_REQUEST, true),
      })
      .pipe(map(jobs => jobs.map(j => this.mapApiJobToJob(j))));
  }

  /** POST /v1/job-postings/{id}/duplicate – duplicate an existing job posting */
  createFromTemplate(id: string): Observable<IdRes> {
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-postings/${id}/duplicate`,
      {}
    );
  }

  /** GET /v1/job-postings/{id}/detail – get job posting detail */
  getById(id: string): Observable<Job> {
    return this.http
      .get<any>(`${this.apiBase}/v1/job-postings/${id}/detail`)
      .pipe(
        map(j => this.mapApiJobToJob(j)),
        mergeMap(job => this.hydrateJobOwnerProfilePictures(job)),
        mergeMap(job =>
          this.resolveJobFileUrlsWithAvatarRecovery([job]).pipe(
            map(jobs => jobs[0])
          )
        )
      );
  }

  /**
   * Owner snapshots on postings can lag; Standorte often have no `profile_picture_id` while
   * the Schuleinheit root has the Schullogo. Loads live owner media via {@link SchoolService}.
   * Never clears an existing posting snapshot when school detail has no avatar row.
   */
  private hydrateJobOwnerProfilePictures(job: Job): Observable<Job> {
    const primaryOwnerId = this.getJobOwnerProfileId(job);
    if (!primaryOwnerId) {
      return of(job);
    }
    return this.schoolService.getById(primaryOwnerId).pipe(
      switchMap((owner: School) => {
        const primaryHadAvatar = !!owner.avatar?.filename;
        if (primaryHadAvatar) {
          job.schoolAvatar = { ...owner.avatar! };
        }
        if (owner.backgroundImage?.filename) {
          job.schoolBackgroundImage = { ...owner.backgroundImage };
        }
        const rootId = job.schoolRootId;
        const shouldTryRoot =
          job.ownerType === 'school' &&
          !!rootId &&
          rootId !== primaryOwnerId &&
          (!job.schoolAvatar?.filename || !primaryHadAvatar);

        if (!shouldTryRoot) {
          return of(job);
        }

        return this.schoolService.getById(rootId).pipe(
          map((root: School) => {
            if (root.avatar?.filename) {
              job.schoolAvatar = { ...root.avatar };
            }
            if (root.backgroundImage?.filename) {
              job.schoolBackgroundImage = { ...root.backgroundImage };
            }
            return job;
          }),
          catchError(() => of(job))
        );
      }),
      catchError(() => of(job))
    );
  }

  private mapApiActivityRanges(
    apiJob: any
  ): Pick<Job, 'activityRangeInPercentage' | 'activityRangeInHours'> {
    return {
      activityRangeInPercentage:
        apiJob.activity_percentage_from != null ||
        apiJob.activity_percentage_to != null
          ? {
              from: apiJob.activity_percentage_from ?? undefined,
              to: apiJob.activity_percentage_to ?? undefined,
            }
          : undefined,
      activityRangeInHours:
        apiJob.activity_hours_from != null || apiJob.activity_hours_to != null
          ? {
              from: apiJob.activity_hours_from ?? undefined,
              to: apiJob.activity_hours_to ?? undefined,
            }
          : undefined,
    };
  }

  /** HR / search cards: enrich jobs missing a resolvable owner file id (see {@link jobNeedsOwnerAvatarHydration}).
   *  Deduplicates school fetches so jobs sharing a school ID only trigger one GET /v1/schools/{id}.
   */
  private enrichJobsWithOwnerAvatars(jobs: Job[]): Observable<Job[]> {
    const targets = jobs.filter(j => this.jobNeedsOwnerAvatarHydration(j));
    if (targets.length === 0) {
      return of(jobs);
    }

    const uniquePrimaryIds = [...new Set(targets.map(j => j.schoolId!))];
    return forkJoin(
      uniquePrimaryIds.map(id =>
        this.schoolService
          .getById(id)
          .pipe(catchError(() => of(null as School | null)))
      )
    ).pipe(
      mergeMap(primarySchools => {
        const primaryMap = new Map<string, School | null>(
          uniquePrimaryIds.map((id, i) => [id, primarySchools[i]])
        );

        // Apply primary school data; collect root IDs for jobs where primary has no avatar
        const rootFallbackTargets: Job[] = [];
        for (const job of targets) {
          const school = primaryMap.get(job.schoolId!);
          if (!school) continue;
          if (school.avatar?.filename) {
            job.schoolAvatar = { ...school.avatar };
          }
          if (school.backgroundImage?.filename) {
            job.schoolBackgroundImage = { ...school.backgroundImage };
          }
          const needsRootFallback =
            !!job.schoolRootId &&
            job.schoolRootId !== job.schoolId &&
            !school.avatar?.filename;
          if (needsRootFallback) {
            rootFallbackTargets.push(job);
          }
        }

        if (rootFallbackTargets.length === 0) {
          return of(jobs);
        }

        const uniqueRootIds = [
          ...new Set(rootFallbackTargets.map(j => j.schoolRootId!)),
        ];
        return forkJoin(
          uniqueRootIds.map(id =>
            this.schoolService
              .getById(id)
              .pipe(catchError(() => of(null as School | null)))
          )
        ).pipe(
          map(rootSchools => {
            const rootMap = new Map<string, School | null>(
              uniqueRootIds.map((id, i) => [id, rootSchools[i]])
            );
            for (const job of rootFallbackTargets) {
              const root = rootMap.get(job.schoolRootId!);
              if (!root) continue;
              if (root.avatar?.filename) job.schoolAvatar = { ...root.avatar };
              if (root.backgroundImage?.filename)
                job.schoolBackgroundImage = { ...root.backgroundImage };
            }
            return jobs;
          })
        );
      })
    );
  }

  private jobNeedsOwnerAvatarHydration(job: Job): boolean {
    const ownerId = this.getJobOwnerProfileId(job);
    if (!ownerId) {
      return false;
    }
    // Backend pre-resolved the full picture chain (own + root fallback).
    // If path is set, we have a URL already. If schoolAvatar is null, backend
    // confirmed no picture exists anywhere — skip the detail call in both cases.
    if (job.schoolAvatar?.path || !job.schoolAvatar) {
      return false;
    }
    // Has a file ID but URL not resolved — needs resolution (non-listing sources).
    return (
      job.ownerType === 'school' &&
      !!job.schoolRootId &&
      !!job.schoolId &&
      job.schoolRootId !== job.schoolId
    );
  }

  private getJobOwnerProfileId(job: Job): string | null {
    if (job.ownerType === 'school_authority' && job.schoolAuthorityId) {
      return job.schoolAuthorityId;
    }
    if (job.schoolId) {
      return job.schoolId;
    }
    return null;
  }

  /** GET /v1/job-postings/{id}/applications – get applications for a job posting */
  getApplications(jobPostingId: string): Observable<unknown[]> {
    return this.http.get<unknown[]>(
      `${this.apiBase}/v1/job-postings/${jobPostingId}/applications`
    );
  }

  /**
   * PUT /v1/job-postings/{id}/close – close or reopen a job posting.
   *
   * Accepts either a boolean (new callers) or a string reason (legacy callers
   * that previously passed a close reason to the old API).  A string value is
   * treated as "close the job" (is_closed = true) for backwards compatibility.
   */
  close(
    id: string,
    isClosedOrReason?: boolean | string
  ): Observable<StatusRes> {
    const isClosed =
      typeof isClosedOrReason === 'boolean' ? isClosedOrReason : true;
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/close`,
      { is_closed: isClosed } satisfies CloseJobPostingReq
    );
  }

  /**
   * PUT /v1/job-postings/{id}/archive – archive or unarchive a job posting.
   * Archiving also closes the posting server-side.
   */
  archive(id: string, isArchived: boolean = true): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/archive`,
      { is_archived: isArchived }
    );
  }

  /** POST /v1/job-postings/{id}/publish – publish a job posting */
  publish(id: string): Observable<StatusRes> {
    return this.http.post<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/publish`,
      {}
    );
  }

  /**
   * Ensures school_levels is always an array in the DB before publishing.
   * The publish endpoint rejects with 400 if school_levels is null, even when
   * no Sekundarstufe II specifications apply. Calls PUT …/education-levels
   * only when education_levels has ≥1 item (API constraint), always including
   * school_levels (empty array is valid).
   */
  ensureSchoolLevelsForPublish(job: Job): Observable<StatusRes | null> {
    const education_levels = (job.schoolLevels ?? [])
      .map(l => REVERSE_EDUCATION_LEVEL_MAP[l])
      .filter(Boolean) as string[];
    const school_levels = (job.schoolLevelDescriptions ?? [])
      .map(l => REVERSE_SCHOOL_LEVEL_MAP[l])
      .filter(Boolean) as string[];

    if (education_levels.length >= 1) {
      return this.updateEducationLevels(job.id!, {
        education_levels,
        school_levels,
      });
    }
    return new Observable(observer => {
      observer.next(null);
      observer.complete();
    });
  }

  /**
   * Persists a lapsed runtime as a real close, before its dates get extended.
   *
   * The API keeps `is_published: true` after `publish_to` passes and never sets
   * `is_closed`, so DEACTIVATED is derived from the dates alone (see
   * {@link resolveJobStatusFromApi}). Extending the runtime would therefore
   * re-publish the posting as a side effect of picking a date. Closing it first
   * makes the `is_closed` branch win over the dates, so going live again takes
   * the explicit "Veröffentlichen" button (`JobTopBarComponent.executeReactivating`,
   * which reopens via `PUT …/close { is_closed: false }`).
   *
   * `isPublic` is `is_published && !is_closed`, so this fires only for a posting
   * that really is live-but-lapsed. A draft carrying a stale `publish_to` derives
   * DEACTIVATED too, but must not be closed — that would promote a draft into a
   * deactivated posting.
   */
  closeIfExpired(job: Job): Observable<StatusRes | null> {
    if (job.id && job.status === JobStati.DEACTIVATED && job.isPublic) {
      return this.deactivate(job.id);
    }
    return new Observable(observer => {
      observer.next(null);
      observer.complete();
    });
  }

  /** PUT /v1/job-postings/{id}/activity – update activity range */
  updateActivity(id: string, body: UpdateActivityReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/activity`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/subjects – update subjects */
  updateSubjects(
    id: string,
    body: UpdateJobPostingSubjectsReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/subjects`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/qualifications – update qualifications */
  updateQualifications(
    id: string,
    body: UpdateQualificationsReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/qualifications`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/education-levels – update education levels */
  updateEducationLevels(
    id: string,
    body: UpdateEducationLevelsReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/education-levels`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/job-title – update job title */
  updateJobTitle(id: string, body: UpdateJobTitleReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/job-title`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/language – set or clear the posting's language */
  updateLanguage(
    id: string,
    body: UpdateContentLanguageReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/language`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/employment-period – update employment period */
  updateEmploymentPeriod(
    id: string,
    body: UpdateEmploymentPeriodReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/employment-period`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/deadline – update application deadline */
  updateDeadline(id: string, body: UpdateDeadlineReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/deadline`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/publish-from – update publish-from date */
  updatePublishFrom(
    id: string,
    body: UpdatePublishFromReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/publish-from`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/publish-to – update publish-to date */
  updatePublishTo(id: string, body: UpdatePublishToReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/publish-to`,
      body
    );
  }

  /**
   * Sets both ends of the publish window.
   *
   * Prefers `PUT /publish-window`, which takes the pair and validates it against
   * itself. That endpoint may not exist yet: frontend and backend images are
   * pulled independently by Watchtower on each VM, so during a release the new
   * bundle can be served for a few minutes against the previous backend. When it
   * is missing we fall back to writing the two single-field endpoints in a safe
   * order — see {@link writePublishWindowSequentially}.
   *
   * @param current the window as last loaded from the API (ms, or null/undefined)
   */
  updatePublishWindow(
    id: string,
    window: { from: number; to: number },
    current: { from?: number | null; to?: number | null }
  ): Observable<Job> {
    if (this.publishWindowUnavailable) {
      return this.writePublishWindowSequentially(id, window, current);
    }

    const body: UpdatePublishWindowReq = {
      publish_from: this.timestampToApiDateString(window.from),
      publish_to: this.timestampToApiDateString(window.to),
    };

    return this.http
      .put<StatusRes>(
        `${this.apiBase}/v1/job-postings/${id}/publish-window`,
        body
      )
      .pipe(
        switchMap(() => this.getById(id)),
        catchError(err => {
          if (!this.isMissingEndpoint(err)) {
            return throwError(() => err);
          }
          // Latch it: every later save this session skips the doomed probe.
          // The flag only ever selects a working path, so a stale one costs
          // nothing beyond an extra request until the next page load.
          this.publishWindowUnavailable = true;
          return this.writePublishWindowSequentially(id, window, current);
        })
      );
  }

  /**
   * True when a 404 means "this route does not exist" rather than "this posting
   * does not exist".
   *
   * The backend answers a domain 404 through its exception handler, which always
   * emits `{ error, err_code }`. An unrecognised route never reaches that
   * handler — no handler is registered for StarletteHTTPException — so FastAPI's
   * default `{ "detail": "Not Found" }` comes back instead.
   *
   * Misreading a genuine "Job posting not found" as a missing route is harmless:
   * the fallback then hits `/publish-from`, gets the same domain 404, and
   * surfaces the right message one request later.
   */
  private isMissingEndpoint(err: unknown): boolean {
    if (!err || typeof err !== 'object' || !('status' in err)) return false;
    if ((err as { status?: unknown }).status !== 404) return false;

    const body = (err as { error?: unknown }).error;
    const domainMessage =
      body && typeof body === 'object'
        ? (body as { error?: unknown }).error
        : undefined;
    return typeof domainMessage !== 'string' || domainMessage.length === 0;
  }

  /**
   * Fallback for backends without `/publish-window`: write the two single-field
   * endpoints one after the other, in an order that never leaves the record
   * inverted.
   *
   * Each endpoint validates against whatever the *other* one currently holds in
   * the DB (`publish_from <= publish_to`, enforced on both sides). Firing them
   * concurrently therefore fails whenever the losing request is checked against
   * a value the winner has not written yet.
   *
   * Neither fixed order is universally safe, so it is chosen from the values the
   * posting currently holds: writing `publish_to` first needs `to >= currentFrom`,
   * writing `publish_from` first needs `from <= currentTo`. Given `from <= to`,
   * at least one always holds — if `to < currentFrom` then
   * `from <= to < currentFrom <= currentTo`, so the from-first branch is valid.
   */
  private writePublishWindowSequentially(
    id: string,
    window: { from: number; to: number },
    current: { from?: number | null; to?: number | null }
  ): Observable<Job> {
    const setFrom = this.updatePublishFrom(id, {
      publish_from: this.timestampToApiDateString(window.from),
    });
    const setTo = this.updatePublishTo(id, {
      publish_to: this.timestampToApiDateString(window.to),
    });

    const toFirstIsSafe = current.from == null || window.to >= current.from;
    const ordered = toFirstIsSafe ? [setTo, setFrom] : [setFrom, setTo];

    // concat, not forkJoin: the second request must see the first one's write.
    return concat(...ordered).pipe(
      last(),
      switchMap(() => this.getById(id))
    );
  }

  /** PUT /v1/job-postings/{id}/work-day-matrix – update work day matrix */
  updateWorkDayMatrix(
    id: string,
    body: UpdateWorkDayMatrixReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/work-day-matrix`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/additional-info – update additional info */
  updateAdditionalInfo(
    id: string,
    body: UpdateJobPostingAdditionalInfoReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/additional-info`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/skill-requirements – update skill requirements */
  updateSkillRequirements(
    id: string,
    body: UpdateJobPostingSkillRequirementsReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/skill-requirements`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/job-tasks – update job tasks */
  updateJobTasks(
    id: string,
    body: UpdateJobPostingJobTasksReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/job-tasks`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/what-we-offer – update what-we-offer section */
  updateWhatWeOffer(
    id: string,
    body: UpdateJobPostingWhatWeOfferReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/what-we-offer`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/contacts – update contacts list */
  updateContacts(
    id: string,
    body: UpdateJobPostingContactsReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/contacts`,
      body
    );
  }

  // --- Gallery ---

  /** POST /v1/job-postings/{id}/gallery – upload a gallery item (image + optional audio/metadata) */
  uploadGalleryItem(
    id: string,
    fileImage: File,
    opts?: { fileAudio?: File; title?: string; description?: string }
  ): Observable<IdRes> {
    const form = new FormData();
    form.append('file_image', fileImage);
    if (opts?.fileAudio) form.append('file_audio', opts.fileAudio);
    if (opts?.title) form.append('title', opts.title);
    if (opts?.description) form.append('description', opts.description);
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-postings/${id}/gallery`,
      form
    );
  }

  /** DELETE /v1/job-postings/{id}/gallery/{galleryId} – delete a gallery item */
  deleteGalleryItem(id: string, galleryId: string): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/gallery/${galleryId}`
    );
  }

  /** PUT /v1/job-postings/{id}/gallery/{galleryId} – update gallery item metadata */
  updateGalleryItem(
    id: string,
    galleryId: string,
    body: UpdateGalleryItemReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/gallery/${galleryId}`,
      body
    );
  }

  /** PUT /v1/job-postings/{id}/gallery/reorder – set display order of gallery items */
  reorderGallery(id: string, body: ReorderGalleryReq): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/gallery/reorder`,
      body
    );
  }

  // --- Documents ---

  /** POST /v1/job-postings/{id}/docs – upload a document */
  uploadDoc(id: string, file: File, title: string): Observable<IdRes> {
    const form = new FormData();
    form.append('file', file);
    form.append('title', title);
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-postings/${id}/docs`,
      form
    );
  }

  /** PUT /v1/job-postings/{id}/docs/{docId} – update document title */
  updateDocTitle(
    id: string,
    docId: string,
    body: UpdateDocTitleReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/docs/${docId}`,
      body
    );
  }

  /** DELETE /v1/job-postings/{id}/docs/{docId} – delete a document */
  deleteDoc(id: string, docId: string): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/docs/${docId}`
    );
  }

  // --- Contact profile picture ---

  /** POST /v1/job-postings/{id}/contacts/{contactId}/profile-picture – upload contact profile picture */
  uploadContactProfilePicture(
    id: string,
    contactId: string,
    file: File
  ): Observable<IdRes> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-postings/${id}/contacts/${contactId}/profile-picture`,
      form
    );
  }

  /** DELETE /v1/job-postings/{id}/contacts/{contactId}/profile-picture – delete contact profile picture */
  deleteContactProfilePicture(
    id: string,
    contactId: string
  ): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/contacts/${contactId}/profile-picture`
    );
  }

  // --- Video ---

  /** POST /v1/job-postings/{id}/video/upload-token – request a video upload token */
  requestVideoUploadToken(id: string): Observable<VideoUploadTokenRes> {
    return this.http.post<VideoUploadTokenRes>(
      `${this.apiBase}/v1/job-postings/${id}/video/upload-token`,
      {}
    );
  }

  /** POST /v1/job-postings/{id}/video/{apiVideoId} – associate uploaded video with job posting */
  uploadVideo(id: string, apiVideoId: string): Observable<IdRes> {
    return this.http.post<IdRes>(
      `${this.apiBase}/v1/job-postings/${id}/video/${apiVideoId}`,
      {}
    );
  }

  /** DELETE /v1/job-postings/{id}/video – delete the video from a job posting */
  deleteVideo(id: string): Observable<StatusRes> {
    return this.http.delete<StatusRes>(
      `${this.apiBase}/v1/job-postings/${id}/video`
    );
  }

  // --- Application review ---

  /** PUT /v1/job-postings/{jobPostingId}/applications/{applicationId}/review – review an application */
  reviewApplication(
    jobPostingId: string,
    applicationId: string,
    body: ReviewApplicationReq
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(
      `${this.apiBase}/v1/job-postings/${jobPostingId}/applications/${applicationId}/review`,
      body
    );
  }

  // --- Compatibility shims for removed methods ---

  /**
   * Create a new job posting.
   *
   * Legacy callers pass a single `Partial<Job>` object that contains `schoolId`
   * (and optionally `isFullTimeJob`, `kind`).  New callers pass
   * `(schoolId, body)` as two separate arguments.  This overload handles both.
   */
  create(
    schoolIdOrJob: string | Partial<Job>,
    body?: CreateJobPostingReq
  ): Observable<Job & IdRes> {
    if (typeof schoolIdOrJob === 'string') {
      // New signature: create(schoolId, body)
      return this.http
        .post<any>(
          `${this.apiBase}/v1/job-postings/schools/${schoolIdOrJob}`,
          body ?? {}
        )
        .pipe(
          map(
            res => ({ ...this.mapApiJobToJob(res), id: res.id }) as Job & IdRes
          )
        );
    }
    // Legacy signature: create(partial) – extract schoolId from the object
    const job = schoolIdOrJob;
    const schoolId = job.schoolId!;
    const jobPostingType =
      job.kind === 'temporary'
        ? 'BEFRISTETE_ANSTELLUNG'
        : job.kind === 'substitute' || job.isFullTimeJob === false
          ? 'STELLVERTRETUNG'
          : 'FESTANSTELLUNG';
    return this.http
      .post<any>(`${this.apiBase}/v1/job-postings/schools/${schoolId}`, {
        job_posting_type: jobPostingType,
      } satisfies CreateJobPostingReq)
      .pipe(
        map(res => ({ ...this.mapApiJobToJob(res), id: res.id }) as Job & IdRes)
      );
  }

  /** POST /v1/job-postings/school-authorities/{school_authority_id} – create a draft posting owned directly by an authority. */
  createForSchoolAuthority(
    authorityId: string,
    body: CreateJobPostingReq
  ): Observable<Job & IdRes> {
    return this.http
      .post<any>(
        `${this.apiBase}/v1/job-postings/school-authorities/${authorityId}`,
        body
      )
      .pipe(
        map(res => ({ ...this.mapApiJobToJob(res), id: res.id }) as Job & IdRes)
      );
  }

  /**
   * Update a job posting with a partial set of Job fields.
   *
   * The REST API has no `PUT /v1/job-postings/{id}` — updates are split across
   * sub-resources (see OpenAPI). This method maps known `Job` fields to those calls.
   */
  update(id: string, job: Partial<Job>): Observable<Job> {
    const calls: Observable<StatusRes>[] = [];

    if (job.function !== undefined) {
      // `function` now resolves from job_title_id (attribute name) too, so only
      // persist a custom title when there is no attribute reference — otherwise
      // we would copy the resolved attribute name into custom_job_title.
      const body: UpdateJobTitleReq = {
        job_title_id: job.jobTitleId !== undefined ? job.jobTitleId : null,
        custom_job_title: job.jobTitleId
          ? null
          : job.function
            ? String(job.function)
            : null,
      };
      calls.push(this.updateJobTitle(id, body));
    }

    if (job.subjectIds !== undefined) {
      calls.push(
        this.updateSubjects(id, { subject_ids: job.subjectIds ?? [] })
      );
    }

    const updatingEmployment =
      job.durationFrom !== undefined ||
      job.durationTo !== undefined ||
      job.startDateType !== undefined ||
      job.isDurationStartNow !== undefined ||
      job.isDurationEndOpen !== undefined;

    if (updatingEmployment) {
      if (job.isFullTimeJob) {
        const fromDate = this.pickerValueToApiDateString(job.durationFrom);
        const startDateType = job.startDateType ?? (fromDate ? 'DATE' : null);
        calls.push(
          this.updateEmploymentPeriod(id, {
            from_date: startDateType === 'DATE' ? fromDate : null,
            to_date: null,
            start_date_type: startDateType,
          })
        );
      } else {
        calls.push(
          this.updateEmploymentPeriod(id, {
            from_date: job.isDurationStartNow
              ? null
              : this.pickerValueToApiDateString(job.durationFrom),
            to_date: job.isDurationEndOpen
              ? null
              : this.pickerValueToApiDateString(job.durationTo),
          })
        );
      }
    }

    if (job.applicationEndDate !== undefined) {
      const deadlineVal = this.pickerValueToApiDateString(
        job.applicationEndDate
      );
      calls.push(this.updateDeadline(id, { deadline: deadlineVal }));
    }

    // Education levels and school levels (specifications) must be sent together.
    // The API requires both in UpdateEducationLevelsReq, and education_levels needs minItems:1.
    if (
      job.schoolLevels !== undefined ||
      job.schoolLevelDescriptions !== undefined
    ) {
      const education_levels = (job.schoolLevels ?? [])
        .map(l => REVERSE_EDUCATION_LEVEL_MAP[l])
        .filter(Boolean);
      const school_levels = (job.schoolLevelDescriptions ?? [])
        .map(l => REVERSE_SCHOOL_LEVEL_MAP[l])
        .filter(Boolean);

      if (education_levels.length >= 1) {
        const req: UpdateEducationLevelsReq = {
          education_levels,
          school_levels,
        };
        // Omitted = keep stored on the backend; only include when the caller
        // explicitly set these on `job` (undefined means "not touched").
        if (job.levelMode !== undefined) req.level_mode = job.levelMode;
        if (job.grades !== undefined) req.grades = job.grades;
        if (job.sek1Models !== undefined) req.sek1_models = job.sek1Models;
        calls.push(this.updateEducationLevels(id, req));
      }
    }

    if (job.requirements !== undefined) {
      const q: string[] = [];
      if (job.requirements.baseYear) q.push('BASE_YEAR_COMPLETED');
      if (job.requirements.diplom) q.push('DIPLOMA');
      if (job.requirements.student) q.push('TRAINING');

      // An empty list clears the minimum qualification.
      calls.push(this.updateQualifications(id, { qualifications: q }));
    }

    if (
      job.activityRangeInPercentage !== undefined ||
      job.activityRangeInHours !== undefined
    ) {
      const body: UpdateActivityReq = {
        activity_percentage_from: job.activityRangeInPercentage?.from ?? null,
        activity_percentage_to: job.activityRangeInPercentage?.to ?? null,
        activity_hours_from: job.activityRangeInHours?.from ?? null,
        activity_hours_to: job.activityRangeInHours?.to ?? null,
      };
      calls.push(this.updateActivity(id, body));
    }

    if (
      job.runtimeStartTimestamp !== undefined &&
      job.runtimeStartTimestamp !== null
    ) {
      calls.push(
        this.updatePublishFrom(id, {
          publish_from: this.timestampToApiDateString(
            job.runtimeStartTimestamp
          ),
        })
      );
    }

    if (
      job.runtimeEndTimestamp !== undefined &&
      job.runtimeEndTimestamp !== null
    ) {
      calls.push(
        this.updatePublishTo(id, {
          publish_to: this.timestampToApiDateString(job.runtimeEndTimestamp),
        })
      );
    }

    if (job.yourTasks !== undefined || job.yourTasks_html !== undefined) {
      const tasks = toApiRichTextPair(job.yourTasks, job.yourTasks_html);
      calls.push(
        this.updateJobTasks(id, {
          job_tasks: tasks.text,
          job_tasks_html: tasks.html,
        })
      );
    }

    if (job.yourSkills !== undefined || job.yourSkills_html !== undefined) {
      const skills = toApiRichTextPair(job.yourSkills, job.yourSkills_html);
      calls.push(
        this.updateSkillRequirements(id, {
          skill_requirements: skills.text,
          skill_requirements_html: skills.html,
        })
      );
    }

    if (
      job.weOffer !== undefined ||
      job.weOffer_html !== undefined ||
      job.whatWeOfferHighlights !== undefined ||
      job.whatWeOfferHighlightIcons !== undefined
    ) {
      const offer = toApiRichTextPair(job.weOffer, job.weOffer_html);
      calls.push(
        this.updateWhatWeOffer(id, {
          what_we_offer: offer.text,
          what_we_offer_html: offer.html,
          what_we_offer_highlights: job.whatWeOfferHighlights,
          what_we_offer_highlight_icons: job.whatWeOfferHighlightIcons,
        })
      );
    }

    if (
      job.additionalInfos !== undefined ||
      job.additionalInfos_html !== undefined ||
      job.socialLinks !== undefined
    ) {
      const info = toApiRichTextPair(
        job.additionalInfos,
        job.additionalInfos_html
      );
      calls.push(
        this.updateAdditionalInfo(id, {
          website_urls: job.socialLinks ?? undefined,
          additional_description: info.text,
          additional_description_html: info.html,
        })
      );
    }

    if (job.workdayMatrix !== undefined) {
      const matrix = this.mapJobWorkdayMatrixToApi(job.workdayMatrix);
      if (matrix) {
        calls.push(this.updateWorkDayMatrix(id, { work_day_matrix: matrix }));
      }
    }

    if (job.contacts !== undefined) {
      const body = this.buildContactsUpdateBody(job);
      if (body) {
        calls.push(this.updateContacts(id, body));
      }
    }

    if (calls.length === 0) {
      return this.getById(id);
    }

    return forkJoin(calls).pipe(
      switchMap(() => {
        if (
          updatingEmployment &&
          !job.isFullTimeJob &&
          (job.isDurationStartNow !== undefined ||
            job.isDurationEndOpen !== undefined)
        ) {
          writeStoredDurationFlags(id, {
            isDurationStartNow: !!job.isDurationStartNow,
            isDurationEndOpen: !!job.isDurationEndOpen,
          });
        }
        return this.getById(id);
      })
    );
  }

  private buildContactsUpdateBody(
    job: Partial<Job>
  ): UpdateJobPostingContactsReq | null {
    if (job.contacts === undefined) return null;

    const new_contacts: NewJobPostingContact[] = [];
    const update_contacts: UpdateJobPostingContactItem[] = [];

    for (const c of job.contacts) {
      const title = c.jobFunction ?? '';
      const email = c.email ?? '';
      const phone = c.phone || null;
      if (c.contactId) {
        update_contacts.push({
          id: c.contactId,
          title,
          email,
          phone,
        });
      } else {
        const userIdForApi = this.contactUserIdForCreate(c.userId);
        // API: only one of user_id or full_name (linked user vs outside contact).
        if (userIdForApi) {
          new_contacts.push({
            user_id: userIdForApi,
            full_name: null,
            title,
            email,
            phone,
          });
        } else {
          new_contacts.push({
            user_id: null,
            full_name: c.name || null,
            title,
            email,
            phone,
          });
        }
      }
    }

    return {
      new_contacts,
      update_contacts,
      remove_contact_ids: job.removeContactIds ?? [],
    };
  }

  /** Prefer `user_id` only for real user ids, not client-generated UUIDs for outside contacts. */
  private contactUserIdForCreate(userId: string | undefined): string | null {
    if (!userId?.trim()) return null;
    if (this.isLikelyClientGeneratedUuid(userId)) return null;
    return userId;
  }

  private isLikelyClientGeneratedUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    );
  }

  /**
   * `PUT …/publish-from` and `…/publish-to` use OpenAPI `format: date` (`YYYY-MM-DD` only).
   * Sending full ISO-8601 datetimes (with time) returns 400:
   * "Datetimes provided to dates should have zero time - e.g. be exact dates".
   * Uses the local calendar date to match the Material datepicker.
   * @param unixMs `Job` runtime timestamp in **milliseconds** (`Date.getTime()`).
   */
  private timestampToApiDateString(unixMs: number): string {
    const d = new Date(unixMs);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private pickerValueToApiDateString(
    value: Date | string | null | undefined
  ): string | null {
    return toApiDateString(value) ?? null;
  }

  private mapJobWorkdayMatrixToApi(
    matrix: Job['workdayMatrix']
  ): UpdateWorkDayMatrixReq['work_day_matrix'] | null {
    if (!matrix?.length) return null;
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
    ] as const;
    const out: Record<string, { am: boolean; pm: boolean }> = {};
    for (let i = 0; i < 5; i++) {
      out[days[i]] = {
        am: matrix[i]?.am ?? false,
        pm: matrix[i]?.pm ?? false,
      };
    }
    return out as UpdateWorkDayMatrixReq['work_day_matrix'];
  }

  /**
   * Permanently delete an archived job posting.
   *
   * Uses DELETE /v1/job-postings/{id} to remove the posting via the REST API.
   */
  delete(id: string): Observable<unknown> {
    return this.http.delete(`${this.apiBase}/v1/job-postings/${id}`);
  }

  /**
   * Deactivate a published job posting (hide from public listings).
   * OpenAPI: {@link close} — `PUT /v1/job-postings/{id}/close` with `is_closed: true`.
   * (`PUT …/activity` only accepts workload fields per {@link UpdateActivityReq}, not `is_active`.)
   */
  deactivate(id: string): Observable<StatusRes> {
    return this.close(id, true);
  }

  /**
   * Reactivate a deactivated job posting (make it visible again).
   * OpenAPI: {@link close} — `PUT /v1/job-postings/{id}/close` with `is_closed: false`.
   */
  reactivate(id: string): Observable<StatusRes> {
    return this.close(id, false);
  }

  /**
   * Discard a draft job posting (permanent delete of draft).
   * Calls are serialised so only one delete runs at a time; this prevents
   * overlapping deletes that cause loading loops or false success.
   *
   * Uses DELETE /v1/job-postings/{id} to remove the draft via the REST API.
   */
  discard(id: string): Observable<unknown> {
    const nextPromise = this.lastDiscardPromise.then(() =>
      firstValueFrom(this.http.delete(`${this.apiBase}/v1/job-postings/${id}`))
    );
    this.lastDiscardPromise = nextPromise.catch(() => undefined);
    return from(nextPromise);
  }

  /**
   * Get all jobs for a school root (Schuleinheit) including all Standort children.
   *
   * Backend `GET /v1/job-postings/schools/{id}` matches `school_id` exactly and
   * does not aggregate over the parent/child hierarchy — postings on Standorte
   * are invisible when querying the Schuleinheit. We fan out: resolve the unit
   * (root + Standorte) via the cached `loadSchoolUnitForPublicList`, then call
   * the job endpoint for each id in parallel and dedupe.
   */
  getBySchoolRoot(
    rootId: string,
    options?: { hydrateFiles?: boolean }
  ): Observable<Job[]> {
    const hydrateFiles = options?.hydrateFiles ?? true;
    return this.schoolService.loadSchoolUnitForPublicList(rootId).pipe(
      catchError(() => of(null as unknown as null)),
      switchMap(merged =>
        this.fetchJobsForUnitIds(rootId, merged, hydrateFiles)
      )
    );
  }

  /**
   * Archived postings for an HR scope (Schuleinheit root or Schulgemeinde
   * authority), in a single request. The backend `for_profile` scope already
   * aggregates root + Standorte (and, for an authority id, the whole
   * Schulgemeinde) and `is_archived=true` filters server-side — so we avoid the
   * per-unit/per-school fan-out and only transfer + hydrate the archived subset.
   * Only owner logos are resolved (one batched `GET /v1/files`), matching the
   * lightweight HR card grid.
   */
  getArchivedForSchoolScope(scopeId: string): Observable<Job[]> {
    if (!scopeId) {
      return of([]);
    }
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${scopeId}`, {
        params: { is_archived: true },
      })
      .pipe(
        map(arr => arr.map(j => this.mapApiJobToJob(j))),
        map(jobs => {
          const byId = new Map<string, Job>();
          for (const j of jobs) {
            if (j.id && j.isDeleted !== true && !byId.has(j.id)) {
              byId.set(j.id, j);
            }
          }
          return [...byId.values()];
        }),
        mergeMap(jobs => this.resolveJobOwnerLogosOnly(jobs))
      );
  }

  /**
   * Collect every school id under a unit (root + Standorte) and fan out the
   * job-posting fetch in parallel. Falls back to `[rootId]` when the unit
   * endpoint is unreachable so the HR view never shows less than before.
   *
   * `hydrateFiles=false` skips the per-job `GET /v1/files/{id}` round-trips
   * for school avatar/background images — callers that only render the HR
   * card grid can rely on the default avatar fallback and avoid the cascade.
   */
  private fetchJobsForUnitIds(
    rootId: string,
    merged: { id?: string; locations?: { id?: string }[] } | null,
    hydrateFiles: boolean = true
  ): Observable<Job[]> {
    const ids = merged
      ? Array.from(
          new Set(
            [merged.id, ...(merged.locations || []).map(l => l.id)].filter(
              (id): id is string => !!id
            )
          )
        )
      : [rootId];

    if (ids.length === 0) {
      return of([]);
    }

    const base$ = forkJoin(
      ids.map(id =>
        this.http
          .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${id}`)
          .pipe(catchError(() => of([] as any[])))
      )
    ).pipe(
      map(arrays => arrays.flat().map(j => this.mapApiJobToJob(j))),
      map(jobs => {
        const byId = new Map<string, Job>();
        for (const j of jobs) {
          if (j.id && j.isDeleted !== true && !byId.has(j.id)) {
            byId.set(j.id, j);
          }
        }
        return [...byId.values()];
      })
    );

    if (!hydrateFiles) {
      return base$.pipe(mergeMap(jobs => this.resolveJobOwnerLogosOnly(jobs)));
    }

    return base$.pipe(
      mergeMap(jobs => this.enrichJobsWithOwnerAvatars(jobs)),
      mergeMap(jobs => this.resolveJobFileUrlsWithAvatarRecovery(jobs))
    );
  }

  /** Schulgemeinde HR overview: includes authority-owned postings plus school-owned postings under each Schuleinheit. */
  getJobsForSchulgemeindePortal(
    authorityId: string,
    options?: { hydrateFiles?: boolean }
  ): Observable<Job[]> {
    const hydrateFiles = options?.hydrateFiles ?? false;
    const authorityOwned$ = this.getBySchoolAuthority(authorityId, {
      hydrateFiles,
    }).pipe(catchError(() => of([] as Job[])));
    const schoolOwned$ = this.schoolService.getByAuthority(authorityId).pipe(
      switchMap(schools => {
        if (schools.length === 0) {
          return of([] as Job[]);
        }
        return forkJoin(
          schools.map(s =>
            this.getBySchoolRoot(s.id!, { hydrateFiles }).pipe(
              catchError(() => of([] as Job[]))
            )
          )
        ).pipe(map(arrays => arrays.flat()));
      })
    );
    return forkJoin([authorityOwned$, schoolOwned$]).pipe(
      map(([authorityJobs, schoolJobs]) => {
        const byId = new Map<string, Job>();
        for (const j of [...authorityJobs, ...schoolJobs]) {
          if (j.id && !j.isDeleted && !byId.has(j.id)) {
            byId.set(j.id, j);
          }
        }
        return [...byId.values()];
      })
    );
  }

  /**
   * Public (anonymous-safe) authority-wide job list for the `/embed/jobs?authorityId=`
   * iframe: all published/open postings across a whole Schulgemeinde — authority-owned
   * + every Schuleinheit + every Standort.
   *
   * Uses only endpoints that serve anonymous visitors:
   * - {@link getBySchool} with `for_profile=true` (public; guests get published-only). The
   *   backend `for_profile` scope aggregates root + Standorte (see the note on
   *   {@link getArchivedForSchoolScope}), so one call per node covers that node's subtree.
   *   This deliberately avoids {@link getBySchoolRoot}'s `GET /v1/schools/{id}/unit`
   *   fan-out, which requires auth (401 for guests), and {@link getBySchoolAuthority},
   *   whose `/v1/job-postings/school-authorities/{id}` endpoint also requires auth.
   * - {@link SchoolService.getByAuthority} (`GET /v1/school-authorities/{id}/schools`,
   *   public) to enumerate the units, each fetched with its own public `for_profile` call
   *   as a safety net in case the authority-level scope does not fully aggregate.
   */
  getBySchoolAuthorityTreePublic(authorityId: string): Observable<Job[]> {
    const publicProfileJobs$ = (id: string): Observable<Job[]> =>
      this.getBySchool(id, { forProfile: true, asPublicVisitor: true }).pipe(
        catchError(() => of([] as Job[]))
      );

    const authorityOwned$ = publicProfileJobs$(authorityId);

    const unitJobs$ = this.schoolService.getByAuthority(authorityId).pipe(
      switchMap(units => {
        const unitIds = units.map(u => u.id).filter((id): id is string => !!id);
        if (unitIds.length === 0) {
          return of([] as Job[]);
        }
        return forkJoin(unitIds.map(id => publicProfileJobs$(id))).pipe(
          map(arrays => arrays.flat())
        );
      }),
      catchError(() => of([] as Job[]))
    );

    return forkJoin([authorityOwned$, unitJobs$]).pipe(
      map(([authorityJobs, unitJobs]) => {
        const byId = new Map<string, Job>();
        for (const j of [...authorityJobs, ...unitJobs]) {
          if (
            j.id &&
            !j.isDeleted &&
            j.isPublic === true &&
            j.isClosed !== true &&
            !byId.has(j.id)
          ) {
            byId.set(j.id, j);
          }
        }
        return [...byId.values()];
      })
    );
  }

  /**
   * Get filtered jobs for a school root (includes Standorte via fan-out).
   * Reuses {@link getBySchoolRoot} so the embed/jobs flow stays consistent
   * with the HR overview when the backend gains a posting on a Standort.
   *
   * Filters via {@link isPubliclyVisible} — the previous `isPublic`/`isClosed`
   * comparison let a posting with a passed `publish_to` stay in the embed widget.
   */
  getBySchoolRootFiltered(rootId: string): Observable<Job[]> {
    return this.getBySchoolRoot(rootId).pipe(
      map(jobs => jobs.filter(job => this.isPubliclyVisible(job)))
    );
  }

  /**
   * Publicly visible jobs for a specific school (not the whole root).
   * Uses the default `for_profile` scope: `for_profile=false` is admin-only and
   * 404s for the anonymous visitors of a customer iframe. The `schoolId` filter
   * below narrows a unit's aggregated scope back to its own postings.
   */
  getBySchoolFiltered(schoolId: string): Observable<Job[]> {
    return this.http
      .get<any[]>(`${this.apiBase}/v1/job-postings/schools/${schoolId}`)
      .pipe(
        map(jobs => jobs.map(j => this.mapApiJobToJob(j))),
        map(jobs =>
          jobs.filter(
            job =>
              job.schoolId === schoolId &&
              this.isPubliclyVisible(job) &&
              job.isDeleted !== true
          )
        ),
        mergeMap(jobs => this.enrichJobsWithOwnerAvatars(jobs)),
        mergeMap(jobs => this.resolveJobFileUrlsWithAvatarRecovery(jobs))
      );
  }

  // --- Search ---

  /** Builds query params for GET /v1/job-postings. */
  private buildJobSearchHttpParams(params: JobSearchParams): HttpParams {
    let httpParams = new HttpParams();
    if (params.query) httpParams = httpParams.set('search', params.query);
    if (params.cantons?.length) {
      for (const c of params.cantons)
        httpParams = httpParams.append('cantons', c);
    }
    if (params.educationLevels?.length) {
      for (const e of params.educationLevels)
        httpParams = httpParams.append('education_levels', e);
    }
    if (params.schoolLevels?.length) {
      for (const s of params.schoolLevels)
        httpParams = httpParams.append('school_levels', s);
    }
    if (params.jobTitles?.length) {
      for (const title of params.jobTitles)
        httpParams = httpParams.append('job_titles', title);
    }
    if (params.schoolId)
      httpParams = httpParams.set('school_id', params.schoolId);
    if (params.schoolAuthorityId)
      httpParams = httpParams.set(
        'school_authority_id',
        params.schoolAuthorityId
      );
    if (params.zipcode) httpParams = httpParams.set('zipcode', params.zipcode);
    if (params.city) httpParams = httpParams.set('city', params.city);
    if (params.latitude != null)
      httpParams = httpParams.set('latitude', params.latitude.toString());
    if (params.longitude != null)
      httpParams = httpParams.set('longitude', params.longitude.toString());
    if (params.radiusInKm != null)
      httpParams = httpParams.set('radius_in_km', params.radiusInKm.toString());
    if (params.jobPostingTypes?.length) {
      for (const t of params.jobPostingTypes)
        httpParams = httpParams.append('job_posting_types', t);
    }
    if (params.grades?.length) {
      for (const g of params.grades)
        httpParams = httpParams.append('grades', g);
    }
    if (params.sek1Models?.length) {
      for (const m of params.sek1Models)
        httpParams = httpParams.append('sek1_models', m);
    }
    if (params.levelMatch)
      httpParams = httpParams.set('level_match', params.levelMatch);
    if (params.fromDate)
      httpParams = httpParams.set('from_date', params.fromDate);
    if (params.toDate) httpParams = httpParams.set('to_date', params.toDate);
    if (params.page != null)
      httpParams = httpParams.set('page', params.page.toString());
    if (params.pageSize != null)
      httpParams = httpParams.set('page_size', params.pageSize.toString());
    return httpParams;
  }

  /**
   * Distinct Berufsbezeichnungen among active postings, most frequent first,
   * counted within the given filters. The title selection itself is not a
   * filter here: each count answers "how many if I tick this".
   */
  getJobTitleFacets(
    params: Omit<JobSearchParams, 'jobTitles' | 'page' | 'pageSize'>
  ): Observable<JobTitleFacet[]> {
    const url = `${this.apiBase}/v1/job-postings/job-title-facets`;
    return this.http
      .get<JobTitleFacetList>(url, {
        params: this.buildJobSearchHttpParams(params),
      })
      .pipe(map(res => res.items ?? []));
  }

  search(params: JobSearchParams): Observable<JobSearchResponse> {
    const url = `${this.apiBase}/v1/job-postings`;
    const httpParams = this.buildJobSearchHttpParams(params);

    return this.http.get<any>(url, { params: httpParams }).pipe(
      map(
        res =>
          ({
            job_postings: (res.job_postings || []).map((j: any) =>
              this.mapApiJobToJob(j)
            ),
            page: res.page,
            page_size: res.page_size,
            total_count: res.total_count,
          }) as JobSearchResponse
      ),
      mergeMap(res =>
        this.enrichJobsWithOwnerAvatars(res.job_postings).pipe(
          mergeMap(() =>
            this.resolveJobFileUrlsWithAvatarRecovery(res.job_postings).pipe(
              map(jobs => ({ ...res, job_postings: jobs }))
            )
          )
        )
      )
    );
  }

  getLatest(pageSize: number = 3): Observable<JobSearchResponse> {
    return this.search({ page: 1, pageSize });
  }

  // --- Pure helper / display methods (unchanged) ---

  isWorkdayMatrixEmpty(job: Job) {
    if (job.workdayMatrix?.length) {
      return job.workdayMatrix.every(day => !day.am && !day.pm);
    }

    return true;
  }

  getLevelsOrder(levels: string[]) {
    return getLevelsOrderUtil(levels, { mergeKUST: true });
  }

  /**
   * «Anzeige auf dem Inserat» lines, exactly as the Stufen editor previews
   * them. Legacy rows (no `levelMode`) go through the editor's own load
   * conversion, so an unconverted posting reads the same as when opened there.
   */
  getJobLevelLines(job: Job): string[] {
    return (
      formatJobLevelLines(job) ??
      formatJobLevelLines({
        ...job,
        ...editorStateToJobPatch(editorStateFromJob(job)),
      }) ??
      []
    );
  }

  /**
   * Header and list cards: prefer resolved owner logo, else first Impressionen image
   * (posting-specific visual when the school snapshot has no usable profile picture).
   */
  getJobPostingDisplayLogoUrl(job: {
    schoolAvatar?: Job['schoolAvatar'] | null;
    media?: Job['media'];
  }): string {
    if (job.schoolAvatar?.path) {
      return job.schoolAvatar.path;
    }
    for (const m of job.media || []) {
      if (m.type === 'audio') {
        continue;
      }
      if (m.path) {
        return m.path;
      }
    }
    return 'assets/defaults/school_avatar.png';
  }

  checkRequiredFields(job: Job): {
    hasFunction: boolean;
    hasSchoolLevels: boolean;
    hasWorkload: boolean;
    hasDuration: boolean;
    hasContacts: boolean;
    hasRuntime: boolean;
    hasQualifications: boolean;
  } {
    let hasDuration = false;

    if (job.isFullTimeJob) {
      hasDuration =
        !!job.durationFrom ||
        job.startDateType === 'IMMEDIATELY' ||
        job.startDateType === 'BY_AGREEMENT';
    } else {
      hasDuration =
        (!!job.durationFrom || !!job.isDurationStartNow) &&
        (!!job.durationTo || !!job.isDurationEndOpen);
    }

    const hasQualifications =
      !!job.requirements &&
      (job.requirements.baseYear ||
        job.requirements.diplom ||
        job.requirements.student);

    return {
      hasFunction: !!job.function,
      hasSchoolLevels: !!job.schoolLevels && job.schoolLevels.length > 0,
      hasWorkload:
        this.isValidJobActivityRange(job.activityRangeInHours) ||
        this.isValidJobActivityRange(job.activityRangeInPercentage),
      hasDuration,
      hasContacts: !!job.contacts?.filter(contact => !!contact.visible).length,
      hasRuntime: !!job.runtimeStartTimestamp && !!job.runtimeEndTimestamp,
      hasQualifications,
    };
  }

  private isValidJobActivityRange(activityRange?: JobActivityRange) {
    return !!activityRange && !!activityRange.from && !!activityRange.to;
  }

  getScheduleText(job: Job) {
    const texts = this.getWorkloadTextParts(job);
    return texts.length
      ? this.text('jobText.workload', { value: texts.join(' | ') })
      : '';
  }

  /** Card subtitle under job type (single string; prefer {@link getJobCardWorkloadLine} + {@link getJobCardDurationLine} for layout). */
  getWorkloadAndDurationText(job: Job): string {
    const workloadText = this.getJobCardWorkloadLine(job);
    const durationText = this.getJobCardDurationLine(job);
    if (workloadText && durationText) {
      return `${workloadText}\n${durationText}`;
    }
    return `${workloadText}${durationText}`.trim();
  }

  getJobCardWorkloadLine(job: Job): string {
    return this.stripTrailingPipe(this.getWorkloadTextParts(job).join(' | '));
  }

  getJobCardDurationLine(job: Job): string {
    return this.stripTrailingPipe(this.getJobDurationText(job));
  }

  private stripTrailingPipe(text: string): string {
    return text.replace(/(?:\s*\|)+\s*$/, '').trim();
  }

  /** Workload as separate lines: percentage first, then Lektionen (either may be missing). `short` abbreviates «28 Lektionen» to «28 L.» for the mobile card. */
  getWorkloadTextParts(job: Job, short = false): string[] {
    const texts: string[] = [];
    const percentage = this.formatActivityPercentageText(
      job.activityRangeInPercentage
    );
    if (percentage) {
      texts.push(percentage);
    }
    const hours = this.formatActivityHoursText(job.activityRangeInHours, short);
    if (hours) {
      texts.push(hours);
    }
    return texts;
  }

  private formatActivityValue(value: number): string {
    return Number.isInteger(value)
      ? `${value}`
      : `${value}`.replace(/\.0+$/, '');
  }

  private formatActivityPercentageText(
    range?: JobActivityRange
  ): string | null {
    const from = range?.from;
    const to = range?.to ?? from;
    if (from == null && to == null) {
      return null;
    }
    const start = from ?? to;
    const end = to ?? from;
    if (start == null) {
      return null;
    }
    if (end == null || start === end) {
      return `${this.formatActivityValue(start)} %`;
    }
    return `${this.formatActivityValue(start)} – ${this.formatActivityValue(end)} %`;
  }

  private formatActivityHoursText(
    range?: JobActivityRange,
    short = false
  ): string | null {
    const from = range?.from;
    const to = range?.to ?? from;
    if (from == null && to == null) {
      return null;
    }
    const start = from ?? to;
    const end = to ?? from;
    if (start == null) {
      return null;
    }
    const value =
      end == null || start === end
        ? this.formatActivityValue(start)
        : `${this.formatActivityValue(start)} – ${this.formatActivityValue(end)}`;
    return this.text(
      short ? 'jobText.lessonsShort' : 'jobText.lessons',
      { value }
    );
  }

  /**
   * Start-date wording for permanent jobs: "Ab sofort", "Nach Vereinbarung" or
   * "Ab <date>". `card` uses the short year (01.10.26) for lists, `header` the
   * full year (01.10.2026) for the job detail page and share texts. Returns ''
   * when nothing is configured yet.
   */
  getPermanentStartText(job: Job, style: 'card' | 'header' = 'card'): string {
    const type =
      job.startDateType ??
      (job.isDurationStartNow
        ? 'IMMEDIATELY'
        : job.durationFrom
          ? 'DATE'
          : null);
    switch (type) {
      case 'IMMEDIATELY':
        return this.text('jobText.immediately');
      case 'BY_AGREEMENT':
        return this.text('jobText.byAgreement');
      case 'DATE': {
        const date = this.datePipe.transform(
          job.durationFrom,
          style === 'header' ? 'dd.MM.yyyy' : 'dd.MM.yy'
        );
        return date ? this.text('jobText.fromDate', { date }) : '';
      }
      default:
        return '';
    }
  }

  private getJobDurationText(job: Job): string {
    const formatShort = 'dd.MM.';
    const formatLong = 'dd.MM.yy';
    const immediatelyText = this.text('jobText.immediately');

    if (job.isFullTimeJob) {
      return this.getPermanentStartText(job, 'card');
    }

    const from = job.isDurationStartNow
      ? immediatelyText
      : this.datePipe.transform(job.durationFrom, formatShort) || '';
    const to = job.isDurationEndOpen
      ? this.text('jobText.untilFurtherNotice')
      : this.datePipe.transform(job.durationTo, formatLong) || '';

    if (!from && !to) {
      return '';
    }
    return `${from} - ${to}`;
  }

  getSubstitutionPeriodText(job: Job) {
    const from = job.isDurationStartNow
      ? this.text('jobText.immediately')
      : this.datePipe.transform(job.durationFrom, 'dd.MM.') || '';
    const to = job.isDurationEndOpen
      ? this.text('jobText.untilFurtherNotice')
      : this.datePipe.transform(job.durationTo, 'dd.MM.yyyy') || '';

    const period = `${from} - ${to}`;

    return this.text('jobText.period', { period });
  }

  async getContacts(id: string): Promise<JobContact[]> {
    const job: Job = (await firstValueFrom(this.getById(id))) as Job;

    const toContact = (u: ApiSchoolPermissionUser): SchoolContact => ({
      userId: u.id,
      name: `${u.first_name} ${u.last_name}`.trim(),
      email: u.email,
      phone: u.phone ?? '',
      jobFunction: '',
      avatar: fileRefToBucketFile(u.profile_picture, u.profile_picture_id),
    });

    const collectAdmins = (
      users: ApiSchoolPermissionUser[]
    ): SchoolContact[] => {
      const seen = new Set<string>();
      return users.reduce<SchoolContact[]>((acc, u) => {
        if (!seen.has(u.id)) {
          seen.add(u.id);
          acc.push(toContact(u));
        }
        return acc;
      }, []);
    };

    // The permissions endpoints carry the account phone but no öffentliche
    // Berufsbezeichnung — that (and a fallback phone) lives on the owner's
    // contact entries. A failure here must not hide suggestions.
    const loadOwnerContacts = async (
      contacts$: Observable<SchoolContactDetail[]>
    ): Promise<Map<string, SchoolContact>> => {
      try {
        const contacts = await firstValueFrom(contacts$);
        return new Map(
          contacts.map(c => [c.user_id, mapSchoolContactDetail(c)])
        );
      } catch (err) {
        console.error('Failed to load owner contacts', err);
        return new Map();
      }
    };

    let admins: SchoolContact[];
    let ownerContacts: Map<string, SchoolContact>;

    if (job.ownerType === 'school_authority') {
      const [perms, contacts]: [
        SchoolAuthorityPermission,
        Map<string, SchoolContact>,
      ] = await Promise.all([
        firstValueFrom(
          this.schoolAuthorityApi.getPermissions(job.schoolAuthorityId!)
        ),
        loadOwnerContacts(
          this.schoolAuthorityApi.getContacts(job.schoolAuthorityId!)
        ),
      ]);
      ownerContacts = contacts;
      admins = collectAdmins([
        ...(perms.owner ? [perms.owner] : []),
        ...perms.members,
      ]);
    } else {
      const [perms, contacts]: [
        SchoolPermissionApiResponse,
        Map<string, SchoolContact>,
      ] = await Promise.all([
        firstValueFrom(this.schoolApi.getPermissions(job.schoolId!)),
        loadOwnerContacts(this.schoolApi.getContacts(job.schoolId!)),
      ]);
      ownerContacts = contacts;
      admins = collectAdmins([
        ...(perms.school_owner ? [perms.school_owner] : []),
        ...(perms.school_authority_owner ? [perms.school_authority_owner] : []),
        ...perms.school_members,
        ...perms.school_authority_members,
        ...perms.root_school_members,
      ]);
    }

    const filteredAdmins = admins
      .filter(
        admin => !job.contacts?.find(contact => contact.userId === admin.userId)
      )
      .map(admin => {
        const ownerContact = ownerContacts.get(admin.userId);
        return ownerContact
          ? {
              ...admin,
              // The account phone wins; the org contact entry is the fallback.
              phone: admin.phone || ownerContact.phone,
              jobFunction: ownerContact.jobFunction,
            }
          : admin;
      });

    // The Ansprechperson-Vorschlag always uses the Berufsbezeichnung from the
    // admin's Fachkräfteprofil; the school-level öffentliche Berufsbezeichnung is
    // only a fallback. Load every admin's profile to resolve it.
    const enrichedAdmins = await Promise.all(
      filteredAdmins.map(async admin => {
        try {
          const summary = await firstValueFrom(
            this.profileApi.getUserById(admin.userId)
          );
          return {
            ...admin,
            jobFunction: resolveAdminJobFunction(
              admin.jobFunction,
              summary.jobs
            ),
          };
        } catch (err) {
          console.error('Failed to load admin Fachkräfteprofil', err);
          return admin;
        }
      })
    );

    return [
      ...enrichedAdmins.map(admin => ({ ...admin, isAdmin: true })),
      ...(job.contacts ?? []),
    ];
  }
}
